import React from "react";

import { Grid } from "@material-ui/core";
import { FieldArray, useFormikContext } from "formik";
import { useDispatch, useSelector } from "react-redux";

import { deleteSaleLot } from "actions";

import { SubtleBadge } from "components/Badge";
import SlimButton, { SlimSecondaryButton } from "components/Button/SlimButton";
import { ConfirmDialog, createModalTitle } from "components/ConfirmDialog";
import { FormCollapse } from "components/Form";
import { Input, Label } from "components/Form/FormikControls";

import { calculateTotalPriceCents, formatDecimal } from "lib";

import { getSkinsSexId } from "selectors/sexes";
import { summarizeLots } from "./lib";

import { FormValues } from "./types";
import { useIsMobile } from "hooks";

const SkinsSummary = () => {
  const { values } = useFormikContext<FormValues>();

  const { saleLots, skinsLots } = values;
  const sexIdsToExludeFromQuantity = [useSelector(getSkinsSexId)];
  const summary = summarizeLots(
    [...saleLots, ...skinsLots],
    sexIdsToExludeFromQuantity,
  );
  const { averageDollarsPerKilo, dollarsPerHead, totalPriceDollars } = summary;

  const skinsSummary = summarizeLots(skinsLots);

  return (
    <>
      {skinsLots.length > 1 && (
        <>
          <Grid item xs={6} sm={4}>
            <Input
              disabled
              name="totalSkins"
              align="right"
              label="Total Skins"
              overrideValue={skinsSummary.totalQuantity}
            />
          </Grid>
          <Grid item xs={6} sm={4}>
            <Input
              disabled
              name="averageSkinsPrice"
              align="right"
              beforeSymbol="$"
              label="Avg Skin Price"
              overrideValue={formatDecimal(skinsSummary.dollarsPerHead)}
            />
          </Grid>
          <Grid item xs={6} sm={4}>
            <Input
              disabled
              name="totalSkinsValue"
              align="right"
              beforeSymbol="$"
              label="Total Skin Value"
              overrideValue={formatDecimal(skinsSummary.totalPriceDollars)}
            />
          </Grid>
        </>
      )}

      <Grid item xs={6} sm={4}>
        <Input
          disabled
          name="averagePricePerKgSkins"
          align="right"
          beforeSymbol="$"
          label="Avg $/kg (inc skins)"
          overrideValue={averageDollarsPerKilo}
        />
      </Grid>

      <Grid item xs={6} sm={4}>
        <Input
          disabled
          name="dollarsPerHeadSkins"
          align="right"
          beforeSymbol="$"
          label="$/Hd (inc skins)"
          overrideValue={formatDecimal(dollarsPerHead)}
        />
      </Grid>

      <Grid item xs={6} sm={4}>
        <Input
          disabled
          name="totalPriceSkins"
          beforeSymbol="$"
          align="right"
          label="Total (inc skins)"
          overrideValue={formatDecimal(totalPriceDollars)}
        />
      </Grid>
    </>
  );
};

/* eslint-disable @typescript-eslint/no-explicit-any -- Sale lot, skinsLot, and index types from form values */
const SkinLotEntry =
  (
    handleClickDelete: { (saleLot: any, index: any): void },
    isMobile: boolean,
  ) =>
  (skinsLot: any, index: any) => {
    /* eslint-enable @typescript-eslint/no-explicit-any */
    const skinValue = calculateTotalPriceCents(skinsLot) || 0;

    const showLabel = index === 0 || isMobile;

    return (
      <React.Fragment key={skinsLot.id || index}>
        <Grid item xs={6} sm={4}>
          <Input
            label={showLabel ? "Skin Quantity" : undefined}
            name={`skinsLots[${index}]quantity`}
            type="number"
          />
        </Grid>
        <Grid item xs={6} sm={4}>
          <Input
            label={showLabel ? "Skin Price" : undefined}
            name={`skinsLots[${index}]unitPrice`}
            beforeSymbol="$"
            type="number"
            decimal
          />
        </Grid>
        <Grid item xs={9} sm={3}>
          <Input
            label={showLabel ? "Skin Value" : undefined}
            name="skinValue"
            beforeSymbol="$"
            disabled
            overrideValue={formatDecimal(skinValue / 100)}
            align="right"
          />
        </Grid>
        <Grid
          item
          xs={3}
          sm={1}
          justifyContent="center"
          alignItems="center"
          container
        >
          {showLabel && <Label>&nbsp;</Label>}
          <SlimSecondaryButton
            type="button"
            backgroundColor="red"
            fullWidth
            onClick={() => handleClickDelete(skinsLot, index)}
            disabled={index === 0 && !skinsLot.id}
          >
            Delete
          </SlimSecondaryButton>
        </Grid>
      </React.Fragment>
    );
  };

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- Default skins lot template props
export const SkinsForm = ({ defaultSkinsLot }: any) => {
  const formikProps = useFormikContext<FormValues>();
  const { values } = formikProps;

  const { skinsLots } = values;
  const [confirmDeleteSaleLot, setConfirmDeleteSaleLot] = React.useState(null);

  const isMobile = useIsMobile();

  const removeSaleLotAtIndex = index => {
    const nextValue = values.skinsLots.filter((_s, i) => i !== index);
    // If there is nothing left, add the default.
    const nextOrDefault =
      nextValue.length === 0 ? [defaultSkinsLot] : nextValue;

    formikProps.setFieldValue("skinsLots", nextOrDefault);
  };

  const closeConfirmDialog = () => setConfirmDeleteSaleLot(null);

  const handleClickDelete = (saleLot, index) => {
    if (!saleLot.id) {
      removeSaleLotAtIndex(index);
    } else {
      setConfirmDeleteSaleLot({ saleLot, index });
    }
  };
  const dispatch = useDispatch();
  const handleDelete = () => {
    const { saleLot, index } = confirmDeleteSaleLot;
    dispatch(deleteSaleLot(saleLot));
    removeSaleLotAtIndex(index);
    closeConfirmDialog();
  };

  return (
    <FormCollapse header={<SubtleBadge>Skins</SubtleBadge>}>
      <FieldArray name="skinsLots">
        {arrayHelpers => (
          <>
            {skinsLots.map(SkinLotEntry(handleClickDelete, isMobile))}
            <Grid item xs={9} sm={11} />
            <Grid item xs={3} sm={1}>
              <SlimButton
                onClick={() => arrayHelpers.push(defaultSkinsLot)}
                type="button"
                fullWidth
              >
                Add line
              </SlimButton>
            </Grid>
          </>
        )}
      </FieldArray>
      <SkinsSummary />
      <ConfirmDialog
        title={createModalTitle("this item")}
        isOpen={confirmDeleteSaleLot}
        onCancel={closeConfirmDialog}
        onDelete={handleDelete}
      />
    </FormCollapse>
  );
};
